import type { CatalogItem, ContentKind } from '@viva/core';
import { createContext, useContext } from 'react';
import type { CategorySlice } from '../category-pages';

/** Where an item was opened from, so the player can zap to its neighbors. */
export interface PlaybackList {
  sourceId: string;
  kind: ContentKind;
  categoryKey: string | null;
  /** Set when the list is "All" narrowed by a language or country filter. */
  categories?: CategorySlice[];
  index: number;
  total: number;
}

export interface PlaybackRequest {
  item: CatalogItem;
  /** Neighbors from the catalog (channel zapping). */
  list?: PlaybackList;
  /** A fixed play order (episodes of a series), with the current position. */
  queue?: { items: CatalogItem[]; index: number };
  /** Episodes: the series they belong to, for "Continue watching". */
  series?: CatalogItem;
}

/** "S1 E2 · Title" for episodes, the plain name otherwise. */
export function displayName(item: CatalogItem): string {
  const { season, episode } = item.data;
  return season !== undefined && episode !== undefined ? `S${season} E${episode} · ${item.name}` : item.name;
}

export const PlaybackContext = createContext<(request: PlaybackRequest) => void>(() => undefined);

export function usePlay(): (request: PlaybackRequest) => void {
  return useContext(PlaybackContext);
}

/** Items the player can start directly; Xtream series open their episode list instead. */
export function isPlayable(item: CatalogItem | undefined): item is CatalogItem & { url: string } {
  return !!item && item.url !== null;
}

/** Whether selecting the item does something: play it, or open its episode list. */
export function isOpenable(item: CatalogItem | undefined): item is CatalogItem {
  return !!item && (item.url !== null || item.kind === 'series');
}
